import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCopy } from 'react-icons/fi';
import {
  fetchReferralsByReferrer,
  fetchUserReferralProfile,
  saveReferralCode,
} from '../../services/referralsApi';
import './ReferralsPage.css';

const STATUS_COPY = {
  paid: { label: 'Pagado', badgeClass: 'is-success' },
  pending: { label: 'Pendiente', badgeClass: 'is-warning' },
};

const DEFAULT_CODE_PREFIX = 'FLOTTE';

const normalizeAlias = value => {
  if (!value) return '';
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
};

const coerceString = value => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value || '');
};

const resolveRefereeName = referral => {
  const referee = referral?.expand?.referee_id || {};
  return (
    referee.companyName ||
    referee.name ||
    referee.legalName ||
    referee.email ||
    referee.username ||
    'Empresa sin nombre'
  );
};

const formatCurrency = value =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const ReferralsPage = ({ user }) => {
  const userId = user?.id || '';
  const [aliasInput, setAliasInput] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [isSavingAlias, setIsSavingAlias] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const aliasInputRef = useRef(null);

  const hasActiveCode = Boolean(referralCode);
  const referralLink = useMemo(() => {
    if (!referralCode) return '';
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://flotte.mx';
    const normalizedOrigin = origin.endsWith('/') ? origin.slice(0, -1) : origin;
    return `${normalizedOrigin}/register?ref=${encodeURIComponent(referralCode)}`;
  }, [referralCode]);

  const hydrateReferrals = useCallback(async () => {
    if (!userId) {
      setHistoryRows([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      const [profile, referrals] = await Promise.all([
        fetchUserReferralProfile(userId),
        fetchReferralsByReferrer(userId),
      ]);
      const resolvedCode = coerceString(profile?.referral_code);
      setReferralCode(resolvedCode);
      setAliasInput(resolvedCode);

      const mappedRows = referrals.map(item => ({
        id: item.id,
        company: resolveRefereeName(item),
        status: item.status || 'pending',
        amount: Number(item.amount) || 0,
      }));
      setHistoryRows(mappedRows);
    } catch (error) {
      setErrorMessage(error?.message || 'No se pudo cargar tu historial de referidos.');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    hydrateReferrals();
  }, [hydrateReferrals, reloadKey]);

  const handleFocusAliasInput = () => {
    if (!aliasInputRef.current) return;
    aliasInputRef.current.focus();
    if (typeof aliasInputRef.current.scrollIntoView === 'function') {
      aliasInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCopyLink = async () => {
    if (!referralLink) {
      handleFocusAliasInput();
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const handleSaveAlias = async event => {
    if (event) {
      event.preventDefault();
    }
    if (!userId) return;

    const normalizedAlias = normalizeAlias(aliasInput);
    const aliasToPersist = normalizedAlias
      ? normalizedAlias
      : `${DEFAULT_CODE_PREFIX}${Math.floor(100 + Math.random() * 900)}`;

    setAliasError('');
    setIsSavingAlias(true);
    try {
      const updated = await saveReferralCode(userId, aliasToPersist);
      const updatedAlias = coerceString(updated?.referral_code);
      setReferralCode(updatedAlias);
      setAliasInput(updatedAlias);
      setCopied(false);
      setReloadKey(value => value + 1);
    } catch (error) {
      setAliasError(error?.message || 'No se pudo guardar tu alias.');
    } finally {
      setIsSavingAlias(false);
    }
  };

  const shareButtonLabel = hasActiveCode ? (copied ? 'Copiado' : 'Copiar link') : 'Copiar link';

  return (
    <main className="referrals-page referrals-page--minimal">
      <div className="referrals-shell referrals-shell--minimal">
        <section className="referrals-hero-simple">
          <p className="referrals-hero-eyebrow">Programa de referidos</p>
          <h1>Invita y Gana</h1>
          <p className="referrals-hero-subtitle">
            Comparte tu código. Tú ganas crédito, ellos ganan descuento.
          </p>
          <div className="referrals-copy-box" aria-live="polite">
            <input
              type="text"
              value={referralLink || 'https://flotte.com/r/TU-CODIGO'}
              readOnly
              placeholder="https://flotte.com/r/TU-CODIGO"
            />
            <button type="button" onClick={handleCopyLink} disabled={!hasActiveCode}>
              <FiCopy aria-hidden="true" />
              <span>{shareButtonLabel}</span>
            </button>
          </div>
          {!hasActiveCode ? (
            <p className="referrals-hero-hint">Genera tu alias para activar tu enlace.</p>
          ) : (
            <p className="referrals-hero-hint">
              {copied ? 'Listo, enlace copiado.' : 'Tu enlace está listo para compartir.'}
            </p>
          )}
        </section>

        {!hasActiveCode ? (
          <section className="referrals-alias-panel">
            <h2>Configura tu alias</h2>
            <p>Usa letras y números para registrar tu nombre o el de tu empresa.</p>
            <form className="referrals-alias-form" onSubmit={handleSaveAlias}>
              <label htmlFor="referral-alias-input">Alias (ej. TRANSDIAZ)</label>
              <div className="referrals-alias-field">
                <input
                  id="referral-alias-input"
                  type="text"
                  ref={aliasInputRef}
                  value={aliasInput}
                  onChange={event => setAliasInput(event.target.value)}
                  placeholder="TRANSDIAZ"
                  maxLength={14}
                  autoComplete="off"
                />
                <button type="submit" disabled={isSavingAlias}>
                  {isSavingAlias ? 'Guardando…' : 'Generar código'}
                </button>
              </div>
              {aliasError ? <p className="referrals-field-error">{aliasError}</p> : null}
            </form>
          </section>
        ) : null}

        {errorMessage ? <div className="referrals-alert">{errorMessage}</div> : null}

        <section className="referrals-history referrals-history--minimal">
          <div className="referrals-history-header">
            <h3>Tus referidos</h3>
            <span className="referrals-history-hint">Actualizado hoy</span>
          </div>
          <div className="referrals-history-table referrals-history-table--minimal">
            <div className="referrals-history-row referrals-history-row--head">
              <span>Nombre</span>
              <span>Estado</span>
              <span>Recompensa</span>
            </div>
            {isLoading ? (
              <div className="referrals-history-row referrals-history-row--empty">
                <span>Cargando historial…</span>
              </div>
            ) : historyRows.length ? (
              historyRows.map(row => {
                const status = STATUS_COPY[row.status] || STATUS_COPY.pending;
                return (
                  <div key={row.id} className="referrals-history-row">
                    <span>{row.company}</span>
                    <span>
                      <span className={`referrals-status-badge ${status.badgeClass}`}>
                        {status.label}
                      </span>
                    </span>
                    <span className="referrals-history-reward">{formatCurrency(row.amount)}</span>
                  </div>
                );
              })
            ) : (
              <div className="referrals-history-row referrals-history-row--empty">
                <span>Todavía no registras referidos.</span>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default ReferralsPage;
